import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";

const P = "/photos/materials";

const MATERIAL_SECTIONS = [
  {
    id: "quartzite",
    title: "Quartzite Countertops",
    short: "Natural stone harder than granite with marble-like beauty. Exceptional heat and scratch resistance.",
    long: "Stunning quartzite countertops for luxury Massachusetts kitchens. Expert fabrication serving Northborough, Hudson, Worcester, and Framingham areas.",
    cover: `${P}/quartzite/luxury-quartzite-home-office-desk.jpg`,
    coverAlt: "Quartzite Countertops in Massachusetts",
    learnMore: "https://www.dreamstoneworks.com/stone-fabricator-near-me",
    photos: [
      { src: `${P}/quartzite/luxury-quartzite-home-office-desk.jpg`, name: "Home Office Desk" },
      { src: `${P}/quartzite/calacatta-quartzite-countertops.jpg`, name: "Calacatta" },
      { src: `${P}/quartzite/cielo-quartzite-countertops.jpg`, name: "Cielo" },
      { src: `${P}/quartzite/cristallo-quartzite-countertops.jpg`, name: "Cristallo" },
      { src: `${P}/quartzite/fantasy-brown-quartzite-countertops.jpg`, name: "Fantasy Brown" },
      { src: `${P}/quartzite/fusion-blue-quartzite-countertops.jpg`, name: "Fusion Blue" },
      { src: `${P}/quartzite/ijen-blue-quartzite-countertops.jpg`, name: "Ijen Blue" },
      { src: `${P}/quartzite/jadore-quartzite-countertops.jpg`, name: "Jadore" },
      { src: `${P}/quartzite/java-blue-quartzite-countertops.jpg`, name: "Java Blue" },
      { src: `${P}/quartzite/maya-quartzite-countertops.jpg`, name: "Maya" },
      { src: `${P}/quartzite/ocean-blue-quartzite-countertops.jpg`, name: "Ocean Blue" },
      { src: `${P}/quartzite/sea-pearl-quartzite-countertops.jpg`, name: "Sea Pearl" },
      { src: `${P}/quartzite/sky-white-quartzite-countertops.jpg`, name: "Sky White" },
      { src: `${P}/quartzite/super-white-premium-quartzite-countertops.jpg`, name: "Super White Premium" },
      { src: `${P}/quartzite/taj-mahal-quartzite-countertops.jpg`, name: "Taj Mahal" },
    ],
  },
  {
    id: "dekton",
    title: "Dekton Countertops",
    short: "Ultra-compact sintered surface with exceptional scratch, stain, and UV resistance. Perfect for indoor and outdoor use.",
    long: "Dekton by Cosentino available at our Northborough showroom. Ultra-compact surfaces for Central Massachusetts homeowners. In-house fabrication in Hudson, MA.",
    cover: `${P}/dekton/dekton-outdoor-spa-jacuzzi-design.jpg`,
    coverAlt: "Dekton Countertops in Massachusetts",
    learnMore: "https://www.dreamstoneworks.com/countertop-installation-near-me",
    photos: [
      { src: `${P}/dekton/dekton-outdoor-spa-jacuzzi-design.jpg`, name: "Outdoor Spa" },
      { src: `${P}/dekton/dekton-awake-slab-northborough-massachusetts-countertops.jpg`, name: "Awake" },
      { src: `${P}/dekton/dekton-dark-grey-dekton-slab-northborough-massachusetts.jpg`, name: "Dark Grey" },
      { src: `${P}/dekton/dekton-kelya-slab-northborough-massachusettsi-countertops.jpg`, name: "Kelya" },
      { src: `${P}/dekton/dekton-light-porcelain-slab-northborough-massachusetts.jpg`, name: "Light Porcelain" },
      { src: `${P}/dekton/dekton-morpheus-slab-northborough-massachusetts-countertops.jpg`, name: "Morpheus" },
      { src: `${P}/dekton/dekton-onirika-daze-slab-northborough-massachusetts.jpg`, name: "Onirika Daze" },
      { src: `${P}/dekton/dekton-pietra-kobe-slab-northborough-massachusetts.jpg`, name: "Pietra Kobe" },
      { src: `${P}/dekton/dekton-sterling-slab-northborough-massachusetts-countertops.jpg`, name: "Sterling" },
      { src: `${P}/dekton/dekton-trance-full-slab-northborough-massachusetts.jpg`, name: "Trance — Full Slab" },
      { src: `${P}/dekton/dekton-trance-porcelain-slab-northborough-massachusetts.jpg`, name: "Trance" },
    ],
  },
  {
    id: "neolith",
    title: "Neolith Countertops",
    short: "Ultra-compact surface known for extreme durability, heat resistance, and modern architectural design. Ideal for countertops, walls, and outdoor applications.",
    long: "Neolith sintered stone surfaces for contemporary Massachusetts homes. Visit our Northborough showroom. Serving Worcester County and MetroWest.",
    cover: `${P}/neolith/modern-neolith-fireplace-wall-design.jpg`,
    coverAlt: "Neolith Countertops in Massachusetts",
    learnMore: "https://www.dreamstoneworks.com/stone-fabricator-near-me",
    photos: [
      { src: `${P}/neolith/modern-neolith-fireplace-wall-design.jpg`, name: "Fireplace Wall" },
      { src: `${P}/neolith/abu-dhabi-white-neolith-countertops.jpg`, name: "Abu Dhabi White" },
      { src: `${P}/neolith/arabesque-neolith-countertops.jpg`, name: "Arabesque" },
      { src: `${P}/neolith/black-obsession-neolith-countertops.jpg`, name: "Black Obsession" },
      { src: `${P}/neolith/calacatta-neolith-countertops.jpg`, name: "Calacatta" },
      { src: `${P}/neolith/calacatta-gold-neolith-countertops.jpg`, name: "Calacatta Gold" },
      { src: `${P}/neolith/calacatta-luxe-neolith-countertops.jpg`, name: "Calacatta Luxe" },
      { src: `${P}/neolith/calacatta-roma-neolith-countertops.jpg`, name: "Calacatta Roma" },
      { src: `${P}/neolith/calista-neolith-countertops.jpg`, name: "Calista" },
      { src: `${P}/neolith/everest-sunrise-neolith-countertops.jpg`, name: "Everest Sunrise" },
      { src: `${P}/neolith/himalayan-crystal-neolith-countertops.jpg`, name: "Himalayan Crystal" },
      { src: `${P}/neolith/metropolitan-neolith-countertops.jpg`, name: "Metropolitan" },
      { src: `${P}/neolith/mont-blanc-neolith-countertops.jpg`, name: "Mont Blanc" },
      { src: `${P}/neolith/sofia-cuprum-neolith-countertops.jpg`, name: "Sofia Cuprum" },
    ],
  },
];

export const MaterialGalleries = () => {
  const [lb, setLb] = useState(null); // { sIdx, pIdx }

  const close = useCallback(() => setLb(null), []);
  const move = useCallback((dir) => {
    setLb((cur) => {
      if (!cur) return cur;
      const n = MATERIAL_SECTIONS[cur.sIdx].photos.length;
      return { ...cur, pIdx: (cur.pIdx + dir + n) % n };
    });
  }, []);

  useEffect(() => {
    if (!lb) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") move(-1);
      if (e.key === "ArrowRight") move(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lb, close, move]);

  const active = lb ? MATERIAL_SECTIONS[lb.sIdx] : null;
  const activePhoto = active ? active.photos[lb.pIdx] : null;

  return (
    <section className="border-t border-zinc-200 bg-zinc-50" data-testid="material-galleries">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {MATERIAL_SECTIONS.map((sec, sIdx) => (
            <div key={sec.id} className="group bg-white border border-zinc-200 overflow-hidden flex flex-col" data-testid={`material-card-${sec.id}`}>
              <button
                type="button"
                onClick={() => setLb({ sIdx, pIdx: 0 })}
                className="relative block aspect-[4/3] overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950"
                aria-label={`View ${sec.title} gallery`}
                data-testid={`material-card-cover-${sec.id}`}
              >
                <img
                  src={sec.cover}
                  alt={sec.coverAlt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <span className="text-white text-xs tracking-[0.3em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    View Gallery
                  </span>
                </div>
              </button>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display text-2xl font-light">{sec.title}</h3>
                <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{sec.short}</p>
                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{sec.long}</p>
                <div className="mt-auto pt-5 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setLb({ sIdx, pIdx: 0 })}
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-zinc-950 border-b border-zinc-300 hover:border-zinc-950 pb-1 transition"
                    data-testid={`material-view-photos-${sec.id}`}
                  >
                    View {sec.photos.length} Photos <ChevronRight size={13} />
                  </button>
                  <Link
                    to={`/booking?ref=materials&title=${encodeURIComponent(sec.title)}&cat=Countertops`}
                    className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-950 underline underline-offset-4 transition"
                    data-testid={`material-learn-more-${sec.id}`}
                  >
                    Ask About This Material <ArrowUpRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {activePhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`${activePhoto.name} — ${active.title}`}
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          data-testid="material-lightbox"
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-5 right-5 md:top-7 md:right-7 text-white/80 hover:text-white p-2"
            aria-label="Close gallery"
            data-testid="material-lightbox-close"
          >
            <X size={28} />
          </button>
          <div className="absolute top-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] uppercase" data-testid="material-lightbox-counter">
            {active.title} · {lb.pIdx + 1} / {active.photos.length}
          </div>
          <button
            type="button"
            onClick={() => move(-1)}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
            aria-label="Previous photo"
            data-testid="material-lightbox-prev"
          >
            <ChevronLeft size={36} />
          </button>
          <img
            src={activePhoto.src}
            alt={`${activePhoto.name} — ${active.title}`}
            className="max-h-[82vh] max-w-[88vw] object-contain"
          />
          <button
            type="button"
            onClick={() => move(1)}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
            aria-label="Next photo"
            data-testid="material-lightbox-next"
          >
            <ChevronRight size={36} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/90 text-sm tracking-[0.15em] uppercase" data-testid="material-lightbox-caption">
            {activePhoto.name}
          </div>
        </div>
      )}
    </section>
  );
};
